import { useState, useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { technicalAuditService } from "@/services/technicalAuditService";
import type { CrawlStatus } from "@/types/technicalAudit";

interface CrawlProgressProps {
	crawlId: string;
	onComplete: (runId: string) => void;
	onCancel: () => void;
}

function formatDuration(seconds: number): string {
	if (seconds < 60) {
		return `${Math.floor(seconds)}s`;
	}
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.floor(seconds % 60);
	return `${minutes}m ${remainingSeconds}s`;
}

export function CrawlProgress({
	crawlId,
	onComplete,
	onCancel,
}: CrawlProgressProps) {
	const [status, setStatus] = useState<CrawlStatus | null>(null);
	const [error, setError] = useState<string | null>(null);

	// Poll for status every 2 seconds
	useEffect(() => {
		let intervalId: NodeJS.Timeout | null = null;

		const poll = async () => {
			try {
				const crawlStatus = await technicalAuditService.getCrawlStatus(crawlId);
				setStatus(crawlStatus);

				if (crawlStatus.status === "completed") {
					onComplete(crawlStatus.run_id || crawlStatus.audit_run_id || crawlId);
					if (intervalId) {
						clearInterval(intervalId);
					}
				} else if (crawlStatus.status === "failed") {
					setError(crawlStatus.error_message || "Crawl failed");
					if (intervalId) {
						clearInterval(intervalId);
					}
				}
			} catch (err) {
				setError("Failed to get crawl status");
				console.error("Error polling crawl status:", err);
				if (intervalId) {
					clearInterval(intervalId);
				}
			}
		};

		poll();
		// NOTE: Increased from 2s to 10s to reduce HTTP/1.1 connection pressure
		// (3 users × 2s polling was creating request storms that saturated servers)
		intervalId = setInterval(poll, 10000);

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [crawlId, onComplete]);

	if (error) {
		return (
			<div className="space-y-4">
				<h3 className="text-lg font-medium text-destructive">Crawl Failed</h3>
				<p className="text-sm text-muted-foreground">{error}</p>
				<Button variant="outline" onClick={onCancel}>
					Close
				</Button>
			</div>
		);
	}

	if (!status) {
		return (
			<div className="space-y-4">
				<h3 className="text-lg font-medium">Loading...</h3>
				<div className="text-sm text-muted-foreground">
					Initializing crawl...
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium mb-2">
					{status.status === "completed"
						? "Crawl Completed"
						: "Crawling in progress..."}
				</h3>
				<p className="text-sm text-muted-foreground">
					{status.status === "completed"
						? "Analysis complete. Issues have been identified."
						: "Please wait while we analyze your website."}
				</p>
			</div>

			{/* Progress bar */}
			<div className="space-y-2">
				<Progress value={status.progress_percent} className="h-2" />
				<p className="text-sm text-muted-foreground text-right">
					{Math.round(status.progress_percent)}%
				</p>
			</div>

			{/* Status details */}
			<div className="space-y-2 text-sm">
				<div className="flex justify-between">
					<span className="text-muted-foreground">Pages crawled:</span>
					<span className="font-medium">
						{status.pages_crawled}
						{status.total_pages > 0 && ` / ${status.total_pages}`}
					</span>
				</div>
				<div className="flex justify-between">
					<span className="text-muted-foreground">Issues found:</span>
					<span className="font-medium">{status.issues_found}</span>
				</div>
				{status.current_url && (
					<div className="flex justify-between">
						<span className="text-muted-foreground">Current page:</span>
						<span
							className="font-medium truncate max-w-[300px]"
							title={status.current_url}
						>
							{status.current_url}
						</span>
					</div>
				)}
				<div className="flex justify-between">
					<span className="text-muted-foreground">Elapsed time:</span>
					<span className="font-medium">
						{formatDuration(status.elapsed_seconds)}
					</span>
				</div>
			</div>

			{status.status !== "completed" && (
				<Button variant="outline" onClick={onCancel} className="w-full">
					Cancel
				</Button>
			)}
		</div>
	);
}
